import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { ApolloError } from '@apollo/client';

import { Loading } from 'components/Loading';
import { Logo } from 'components/Logo';

import { LoginStyle, LoginTopHalfStyle } from 'styles/pages/login.styles';
import { TotemLoadingStyle } from 'styles/pages/totem.styles';
import { useAuthenticateEmployeeValidationMutation } from 'data/__generated__';

interface EmployeeEmailValidationProps {
    setSkip?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const EmployeeEmailValidation: React.FC<EmployeeEmailValidationProps> = () => {
    const { token } = useParams<{ token?: string }>();
    const [displayMessage, setDisplayMessage] = useState<string | null>(null);
    const [authenticateEmployeeValidation] = useAuthenticateEmployeeValidationMutation();
    const navigate = useNavigate();

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                return;
            }
            const result = await authenticateEmployeeValidation({ variables: { token } }).catch((e: ApolloError) => {
                // Un lien invalide ou expiré revient dans `error` ; un refus d'accès, lui, est une erreur GraphQL.
                if (e.graphQLErrors?.some(({ extensions }) => extensions?.code === 'AHEAD')) {
                    setDisplayMessage("Seul un administrateur de l'entreprise peut valider ce rattachement.");
                } else {
                    setDisplayMessage('Un problème est survenu lors de la validation de cette adresse email.');
                }
            });

            if (result) {
                const response = result.data?.authenticateEmployeeValidation;
                if (response?.success) {
                    setDisplayMessage("L'employé.e a été bien rattaché.e à votre entreprise");
                    toast.success("L'employé.e a été bien rattaché.e à votre entreprise", {
                        duration: 8000,
                    });
                    navigate('/settings/team', { relative: 'path' });
                } else {
                    setDisplayMessage(response?.error ?? 'Erreur inconnue');
                }
            }
        };
        if (!displayMessage) {
            verifyToken();
        }
    }, [displayMessage, token, authenticateEmployeeValidation, navigate]);

    if (!displayMessage) {
        return (
            <TotemLoadingStyle>
                <Loading />
            </TotemLoadingStyle>
        );
    }

    return (
        <LoginStyle>
            <LoginTopHalfStyle>
                <Logo dark />
            </LoginTopHalfStyle>
            <EmployeeConfirmationContainer>{displayMessage}</EmployeeConfirmationContainer>
        </LoginStyle>
    );
};

const EmployeeConfirmationContainer = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    font-size: 3em;
    padding: 0 5rem;
`;
